import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { LogOut, MoreVertical, Shield, Settings, Menu, Bug, Sparkles, ChevronDown, Lightbulb } from "lucide-react";
import databullLogo from "@/assets/databulllogo.png";
import { APP_NAME } from "@/lib/brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { DATA_GRAPH_ON_NAV } from "@/lib/features";
import HowItWorksDialog from "@/components/HowItWorksDialog";
import IssueDialog from "@/components/IssueDialog";

const allNavItems = [
  { label: "Research", path: "/research", viewerAllowed: false },
  { label: "Agentic Workflows", path: "/aiagents", viewerAllowed: false },
];

const dataSetupPaths = [
  "/data-connectors",
  "/sync-monitor",
  "/inbound-mcp-servers",
  "/data-governance",
  "/observability",
  ...(DATA_GRAPH_ON_NAV ? ["/data-graph"] : []),
  "/deployment",
];

const DataSetupMenu = ({ navigate }: { navigate: (path: string) => void }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isActive = dataSetupPaths.some((p) => location.pathname.startsWith(p));

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={cn(
          "flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
          isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground",
        )}
      >
        Data Control
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-md border border-border bg-popover p-1 shadow-md">
          <button
            onClick={() => {
              navigate("/sync-monitor");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
              location.pathname === "/sync-monitor" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
            )}
          >
            Data Syncing
          </button>
          <button
            onClick={() => {
              navigate("/inbound-mcp-servers");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
              location.pathname === "/inbound-mcp-servers"
                ? "bg-accent text-accent-foreground"
                : "text-popover-foreground",
            )}
          >
            MCP Servers
          </button>
          <button
            onClick={() => {
              navigate("/data-governance");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
              location.pathname === "/data-governance" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
            )}
          >
            Data Governance
          </button>
          <button
            onClick={() => {
              navigate("/observability");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
              location.pathname === "/observability" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
            )}
          >
            Data Observability
          </button>
          {DATA_GRAPH_ON_NAV && (
            <button
              onClick={() => {
                navigate("/data-graph");
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                location.pathname === "/data-graph" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
              )}
            >
              Data Graph
            </button>
          )}
          <button
            onClick={() => {
              navigate("/deployment");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
              location.pathname === "/deployment" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
            )}
          >
            Deployment
          </button>
        </div>
      )}
    </div>
  );
};

const dashboardReportsPaths = ["/dashboards", "/reports", "/queries"];

const DashboardReportsMenu = ({ navigate, isViewer }: { navigate: (path: string) => void; isViewer: boolean }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isActive = dashboardReportsPaths.some((p) => location.pathname.startsWith(p));

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={cn(
          "flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
          isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground",
        )}
      >
        Dashboard & Reports
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-md border border-border bg-popover p-1 shadow-md">
          <button
            onClick={() => {
              navigate("/dashboards");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
              location.pathname === "/dashboards" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
            )}
          >
            Dashboards
          </button>
          {!isViewer && (
            <>
              <button
                onClick={() => {
                  navigate("/reports");
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                  location.pathname === "/reports" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
                )}
              >
                Reports
              </button>
              <button
                onClick={() => {
                  navigate("/queries");
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                  location.pathname === "/queries" ? "bg-accent text-accent-foreground" : "text-popover-foreground",
                )}
              >
                Queries
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const TopNav = () => {
  const { user, profile, clientInfo } = useAuth();
  const navigate = useNavigate();
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const isViewer = profile?.viewer && !profile?.admin && !profile?.super_admin;
  const navItems = isViewer ? allNavItems.filter((item) => item.viewerAllowed) : allNavItems;

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4 md:px-6">
      <div className="flex items-center gap-2 md:gap-6">
        <a href="/research" className="flex items-center gap-2 cursor-pointer">
          <img
            src={clientInfo?.logo_url || databullLogo}
            alt={APP_NAME}
            className="h-8 w-8 rounded-lg object-cover shrink-0"
          />
          <div className="flex flex-col">
            <span className="text-lg font-bold tracking-tight text-foreground leading-tight">{APP_NAME}</span>
            {clientInfo?.name && (
              <span className="text-xs text-muted-foreground leading-tight hidden sm:block">{clientInfo.name}</span>
            )}
          </div>
        </a>
        {/* Desktop nav - hidden on mobile */}
        <nav className="hidden md:flex items-center gap-1">
          {profile?.admin && <DataSetupMenu navigate={navigate} />}
          {navItems.map((item, index) => (
            <React.Fragment key={item.path}>
              <NavLink
                to={item.path}
                className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                activeClassName="bg-accent text-accent-foreground"
              >
                {item.label}
              </NavLink>
              {index === 0 && <DashboardReportsMenu navigate={navigate} isViewer={!!isViewer} />}
            </React.Fragment>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-1 md:gap-3">
        {/* Mobile nav hamburger - visible only on mobile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden">
              <Menu className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 bg-popover z-50">
            {profile?.admin && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Data Setup</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => navigate("/data-connectors")}>Data Connectors</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/sync-monitor")}>DataBull Datacenter</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/data-governance")}>Data Governance</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/observability")}>Data Observability</DropdownMenuItem>
                  {DATA_GRAPH_ON_NAV && <DropdownMenuItem onClick={() => navigate("/data-graph")}>Data Graph</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => navigate("/deployment")}>Deployment</DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {navItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <DropdownMenuItem onClick={() => navigate(item.path)}>{item.label}</DropdownMenuItem>
                {index === 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>Dashboard & Reports</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => navigate("/dashboards")}>Dashboards</DropdownMenuItem>
                      {!isViewer && (
                        <>
                          <DropdownMenuItem onClick={() => navigate("/reports")}>Reports</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate("/queries")}>Queries</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
              </React.Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {user && (
          <>
            {/* Advanced Features star icon */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/advanced-features")}
              title="Advanced Features"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
            {/* Welcome text - hidden on small screens */}
            <span className="hidden sm:block text-sm text-muted-foreground">
              Welcome, <span className="font-medium text-foreground">{profile?.full_name || user.email}</span>
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover z-50">
                {profile?.admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Settings className="h-4 w-4 mr-2" />
                    Admin
                  </DropdownMenuItem>
                )}
                {profile?.super_admin && (
                  <DropdownMenuItem onClick={() => navigate("/superadmin")}>
                    <Shield className="h-4 w-4 mr-2" />
                    Super Admin
                  </DropdownMenuItem>
                )}
                {(profile?.admin || profile?.super_admin) && <DropdownMenuSeparator />}
                <DropdownMenuItem onClick={() => setHowItWorksOpen(true)}>
                  <Lightbulb className="h-4 w-4 mr-2" />
                  How does it work?
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIssueDialogOpen(true)}>
                  <Bug className="h-4 w-4 mr-2" />
                  Report Issue
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => supabase.auth.signOut()}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>

      <IssueDialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen} />
      <HowItWorksDialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />
    </header>
  );
};

export default TopNav;
